/**
 * The admin (Console) settings information architecture — ONE declaration,
 * three consumers.
 *
 * Issue #91, epic #90. The admin surface used to be two tab-strip pages
 * (`SystemSettingsPage` with three tabs, `UserManagementPage` with two) plus a
 * separate list of rail destinations in `config/destinations.ts`. That is the
 * same shape of mistake issue #55 already fixed once for library navigation:
 * when the page, the menu, and the rail each keep their own list of what
 * exists and who may see it, three gates give three answers, and a user ends
 * up with a reachable page, a menu entry pointing at it, and no rail row.
 *
 * So the IA is declared here exactly once and read by:
 *
 *   1. `SettingsHubPage`            — the card grid, and the phone drill-down
 *   2. `NavigationRail` Console mode — the rail's contents on any `/admin/*`
 *   3. `AppBar`                      — resolving an admin route to its title
 *
 * "Console mode invents no new admin IA" is enforced structurally rather than
 * by convention: there is one array, so a card added here appears in all three
 * surfaces, and none of them can drift from the others.
 *
 * `Icon` is declared as a COMPONENT, never as a rendered element — exactly as
 * `config/destinations.ts` does, and for the same reason. The hub draws it at
 * 40px and the rail at ~20px, so the size cannot be baked in at declaration
 * time. Storing `<AdminIcon />` here would freeze it at the default size and
 * make every consumer clone the element to resize it.
 *
 * Why `.tsx` when the file holds no JSX: the icon values are React component
 * types, and keeping the extension consistent with the rest of the config
 * surface means adding a rendered fallback later is not a file rename.
 */

import type { SettingsSectionDef } from '@marinoscar/platform-web/settings/ui';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ModelTrainingOutlinedIcon from '@mui/icons-material/ModelTrainingOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import { identityAdminSections } from '@marinoscar/platform-web/identity/ui';
import { kvoxTranscriptionAdminSections } from '@marinoscar/kvox-web/transcription/ui';
// Doctor (#464, epic #462) — the platform's health-check page, declared by the
// package (title, description, path, `system_settings:read`) and appended below.
import { doctorSettingsPage } from '@marinoscar/platform-web/doctor/ui';
// Webhooks and Alerts (#795): cards from the slices as data.
import { webhooksAdminSections } from '@marinoscar/platform-web/webhooks/ui';
import { alertsAdminCards } from '@marinoscar/platform-web/alerts/ui';
import { setupGuideSettingsPage } from '@marinoscar/platform-web/onboarding/ui';
// Factory reset (#562; a packaged page: card and icon from its descriptor) and
// the label of the `Danger Zone` group both registries pin last.
import { DANGER_ZONE_GROUP_LABEL } from '@marinoscar/platform-web/user-data/headless';
import { factoryResetSettingsPage } from '@marinoscar/platform-web/user-data/ui';
// Telemetry (#471, epic #467) — the platform's three Observability cards,
// declared by the package (data only, so importing them pulls no page into the
// main chunk). Each carries the exact permission the platform's telemetry
// controllers enforce (`telemetry:read` for the settings page, `telemetry:query`
// for the Explorer and the Dashboard) and, on the last two, `feature:
// 'telemetry'`, which `config/settingsFeatures.ts` resolves.
import { telemetryAdminCards } from '@marinoscar/platform-web/telemetry/ui';
// Operations (#266, epic #254). One icon per card, including the two cards
// whose pages land in later issues — the card is declared now, so its icon is
// declared now; see the `Operations` section's own header.
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
// Broadcasts (#325, epic #319) — the one Operations card that is not a view
// onto machinery, but an action taken through it.
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
// About (#126, epic #118) — what is deployed here: version, revision, when it
// was installed and updated, and the server it runs on.
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// Setup (#278, epic #271) — the first-run checklist. Shares its icon with the
// per-user `Getting Started` card in `userSettingsSections.tsx` on purpose: the
// two are the same idea on two axes, and an administrator who has seen one
// recognises the other.
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';

/**
 * The admin sections, in hub order.
 *
 * GATING IS BY PERMISSION, NOT BY ROLE — a role check here is what produced
 * the split-brain described in `destinations.ts`'s header. The strings are the
 * ones the API enforces:
 *
 *   - `system_settings:read`  → `system-settings.controller.ts` (GET)
 *   - `system_settings:write` → `system-settings.controller.ts` (PUT/PATCH)
 *   - `users:read`            → `users.controller.ts`
 *   - `jobs:read`             → `jobs/job-admin.controller.ts`
 *   - `nodes:read`            → the worker-node controller (#267)
 *   - `db_backup:read`        → the database-backup controller (#268)
 *   - `push:read`             → the push-config controller (#355)
 *   - `storage_config:read`   → the platform's storage-config controller
 *                               (`@marinoscar/platform-api/storage`)
 *   - `ai_config:read`        → the platform's AI admin controllers
 *                               (`@marinoscar/platform-api/ai`: config, models,
 *                               usage) and `GET /api/ai-tasks/settings`
 *
 * `Users & Allowlist` gates on `users:read` alone even though it hosts data
 * from two controllers (Users → `users:read`, Allowlist → `allowlist:read`).
 * That mirrors the existing destination gate: the CARD gate is about
 * reachability, and the page is worth reaching for its Users half alone; the
 * Allowlist half gates itself on `allowlist:read` inside the page.
 */
export const ADMIN_SECTIONS: SettingsSectionDef[] = [
  {
    label: 'General',
    cards: [
      {
        // Issue #278, epic #271; the platform's Setup guide since #481. FIRST IN `General`, AND
        // THEREFORE FIRST IN THE HUB, which is the only thing about this card's position that is
        // load-bearing: a fresh deployment's administrator faces a dozen cards across three groups
        // and nothing in that grid says the product cannot transcribe or generate anything until a
        // few of them are filled in. The hub is the right shape for an operator who knows what they
        // came for and the wrong one for somebody on minute one, so the answer to "what do I do
        // first" is literally the first thing in it.
        //
        // The card is the package's (`setupGuideSettingsPage.card`), so its title, text and path
        // are the page's own words, and its permission is `system_settings:read`: the exact string
        // the platform onboarding slice adds the `admin` block of `GET /api/onboarding` for by
        // default (no `onboarding:read` exists, following epic #118 decision 8's precedent for the
        // About card). The registry never invents a permission, it mirrors one.
        //
        // GENERAL, NOT OPERATIONS. `Operations` is the running system: work in flight, the machines
        // executing it, the backups taken while it ran. Setup is configuration an administrator
        // SETS, which is what `General` means; and every step on the page links to a `General` card.
        //
        // ⚠ THE CARD DOES NOT DISAPPEAR ONCE SETUP IS COMPLETE, and that is a decision rather than
        // an omission. The checklist is DERIVED on every read and never stored, so it is the one
        // page that can answer "is this deployment still configured" after a provider key has been
        // rotated away, and a card that vanishes is a card nobody can use to ask.
        ...setupGuideSettingsPage.card,
        Icon: setupGuideSettingsPage.Icon,
      },
      {
        // Issue #124, epic #109. `system_settings:read` is the string
        // the platform's `email-settings.controller` enforces on its GET (#470), exactly as the
        // `Notifications` card below mirrors `system-settings.controller.ts` —
        // the registry never invents a permission. Saving and test-sending need
        // `system_settings:write`, which the PAGE gates internally: the card
        // gate is about REACHABILITY, and a read-only admin diagnosing "why is
        // mail broken" is worth letting in to look.
        title: 'Email',
        description:
          'Choose how the application sends email, and send a test message to prove it works.',
        Icon: EmailOutlinedIcon,
        path: '/admin/settings/email',
        permission: 'system_settings:read',
      },
      {
        // Issue #225, epic #215. `system_settings:read` is the string
        // `system-settings.controller.ts` enforces on its GET, because this
        // setting lives in the system settings document. Saving needs
        // `system_settings:write`, which the PAGE gates internally: the card gate is about REACHABILITY, and "are
        // browser notifications on for this deployment, and which events are
        // suppressed" is worth reading for anyone answering "why did nobody get
        // notified".
        title: 'Notifications',
        description:
          'Turn browser notifications on or off for everyone, and suppress individual events.',
        Icon: NotificationsActiveOutlinedIcon,
        path: '/admin/settings/notifications',
        permission: 'system_settings:read',
      },
      {
        // Issue #355. `push:read` / `push:write` is a permission pair OF ITS
        // OWN, not a reuse of `system_settings:*`: generating/rotating key
        // material has a real, described blast radius (every existing
        // subscriber goes dark until it re-subscribes) that should not ride
        // along with routine settings edits, mirroring why `broadcasts:*` and
        // `nodes:*` were split out rather than folded into
        // `system_settings:*`/`jobs:*`. `push:read` is the string
        // the platform's `push-config.controller` (#470) enforces on its GET — the registry
        // never invents a permission, it mirrors one. Generating, rotating,
        // enabling/disabling and removing all need `push:write`, which the
        // PAGE gates internally: the card gate is about REACHABILITY, and "is
        // web push configured, and by whom" is worth reading for anyone
        // diagnosing why push notifications are not arriving.
        title: 'Web Push',
        description:
          'Generate a VAPID key pair, enable or rotate it, and control whether this deployment can send browser push notifications.',
        Icon: VpnKeyOutlinedIcon,
        path: '/admin/settings/push',
        permission: 'push:read',
      },
      // Transcription (#23, epic #19; the slice's since #499, epic #498): the card
      // is DATA exported by `@marinoscar/kvox-web/transcription/ui`, spread HERE so
      // it keeps its position in the General section. Its `permission` is
      // `system_settings:read`, the exact string `transcription-settings.controller.ts`
      // enforces on its GET; the comment explaining why it is not a pair of its own
      // travels with the data, in the slice's `settings.ts`.
      ...kvoxTranscriptionAdminSections.general,
      {
        // Issue #55, epic #45; the platform's since #477 (epic #472). The page is
        // the platform AI slice's `AiConfigPage` (`@marinoscar/platform-web/ai/ui`),
        // with this app's task-model, graph and ceiling sections in its
        // `Sections` slot. `ai_config:read` is the exact string the platform's
        // `ai-admin.controller` enforces on `GET /api/admin/ai/config`
        // (`ai_config:write` on the writes, which the PAGE gates by disabling
        // its controls), and the app's own `GET /api/ai-tasks/settings`. It
        // replaced `system_settings:read`: the AI policy has a permission pair
        // of its own now, because a wrong change reaches every user of the
        // deployment at once. An admin role seeded before #473 must hold it.
        //
        // ⚠ NO `feature` ON THIS CARD, AND THAT IS THE RULE (AI rule 5). This is
        // where AI is switched on, so it must stay reachable while AI is off;
        // the `AI models` and `AI usage` cards below, and the user's `AI
        // Provider` card, carry `feature: 'ai'` and are hidden until it is on.
        //
        // The page holds no key of the caller's: every AI key a user spends is
        // their own (`/settings/ai`), and the deployment may hold provider keys
        // under its key policy, written only through the platform page.
        title: 'AI',
        description:
          'Switch AI on, choose the key policy and providers, and set which model each task runs on and the ceilings every generation runs under.',
        Icon: AutoAwesomeOutlinedIcon,
        path: '/admin/settings/ai',
        permission: 'ai_config:read',
      },
      {
        // Issue #258, epic #254. `system_settings:read` is the string
        // the platform's maintenance controller (`@marinoscar/platform-api/host`)
        // enforces on its GET —
        // the registry never invents a permission, it mirrors one. That
        // controller deliberately adds NO permission of its own: a maintenance
        // window IS a system setting, stored in the `maintenance` namespace of
        // that row and nowhere else, so `system_settings:read` / `:write`
        // already mean exactly what this page needs them to mean.
        //
        // Opening and closing a window needs `system_settings:write`, which the
        // PAGE gates internally by disabling its controls: the card gate is
        // about REACHABILITY, and "is this deployment deliberately out of
        // service, and which layer is deciding that" is worth reading for
        // anyone answering "why is nothing working".
        title: 'Maintenance',
        description:
          'Take the application out of service for planned work, with a message for anyone who tries to use it.',
        Icon: BuildCircleOutlinedIcon,
        path: '/admin/settings/maintenance',
        permission: 'system_settings:read',
      },
      {
        // Issue #126, epic #118 (decision 8). `system_settings:read` is the
        // string `about/about.controller.ts` enforces on its one GET — the
        // registry never invents a permission, it mirrors one. There is
        // deliberately NO `about:read`: "what is deployed here" is an
        // administrator's configuration read, and a permission no role is
        // seeded with would be a card nobody can open.
        //
        // GENERAL, NOT OPERATIONS, and it is worth being precise about why,
        // because About is strictly neither: `General` is configuration an
        // administrator SETS and `Operations` is the running system, and this
        // page sets nothing and shows no work in flight. It sits here because
        // its permission IS the General permission — an Operations card must
        // carry one of the strings the Operations controllers enforce
        // (`jobs:read`, `nodes:read`, …), none of which the About controller
        // checks, and a card gated on evidence unrelated to whether its request
        // will be authorized is the split-brain this registry exists to
        // prevent. And it is the card an administrator opens first on a new
        // server, which is a General question ("what is this?") before it is
        // an operational one. Read-only by nature: there is no `:write` gate
        // to mention, and triggering an update from the browser is out of
        // scope for the epic permanently.
        title: 'About',
        description:
          'What is running here: version, revision, when it was installed and last updated, and the server it runs on.',
        Icon: InfoOutlinedIcon,
        path: '/admin/settings/about',
        permission: 'system_settings:read',
      },
      {
        // Issue #469, epic #467. The card is the platform storage slice's page
        // (`StorageConfigPage`, `@marinoscar/platform-web/storage/ui`), APPENDED
        // last in `General` so no card a reader has learnt moves.
        // `storage_config:read` is the exact string the platform's
        // `storage-config.controller` enforces on `GET /api/admin/storage-config`
        // (the write routes need `storage_config:write`, which the page gates by
        // disabling its controls). It is deliberately NOT `storage:read`: that
        // pair gates OBJECT access and every Viewer and Contributor holds it,
        // whereas this page edits the credential-bearing configuration of every
        // upload, avatar and backup in the deployment.
        title: 'Storage',
        description:
          'Where uploaded files, avatars and backups are kept: choose the provider, enter its bucket and credentials, and test the connection.',
        Icon: CloudOutlinedIcon,
        path: '/admin/settings/storage',
        permission: 'storage_config:read',
      },
      {
        // Issue #477, epic #472. The platform AI slice's `AiModelsPage`,
        // APPENDED last in `General` so no card a reader has learnt moves.
        // `ai_config:read` is the string the platform's `ai-admin.controller`
        // enforces on `GET /api/admin/ai/models` (`ai_config:write` on enabling
        // a model, refreshing the catalogue and editing a model's limits, which
        // the page gates by disabling its controls). `feature: 'ai'`: with AI
        // off there is no catalogue worth showing, and the `AI` card above is
        // where an admin switches it back on.
        title: 'AI models',
        description:
          'The model catalogue: what each model can do, which ones users may call, and each model’s limits.',
        Icon: ModelTrainingOutlinedIcon,
        path: '/admin/settings/ai/models',
        permission: 'ai_config:read',
        feature: 'ai',
      },
      {
        // Issue #477, epic #472. The platform AI slice's `AiUsagePage`.
        // `ai_config:read` is the string `ai-usage-admin.controller` enforces
        // on `GET /api/admin/ai/usage` (every user's usage, by user, model,
        // provider, key source or feature). `feature: 'ai'`, like `AI models`.
        // Nested UNDER the `AI` route, which `settingsPageTitle`'s
        // longest-prefix rule titles "AI usage", not "AI".
        title: 'AI usage',
        description:
          'Calls, tokens and cost across the deployment, grouped by user, model, provider or feature.',
        Icon: InsightsOutlinedIcon,
        path: '/admin/settings/ai/usage',
        permission: 'ai_config:read',
        feature: 'ai',
      },
    ],
  },
  {
    label: 'Access',
    // Issue #465, epic #462. The card is the identity slice's own registry
    // entry (`Users & Allowlist`, `/admin/settings/users`, `users:read`), spread
    // where the literal card was, so the hub, the Console rail and the AppBar
    // title resolver see exactly what they saw before. The platform's
    // Organization and Organizations cards (`identityAdminSections.organizations`)
    // are deliberately NOT spread: they are `feature: 'orgs'` cards of
    // multi-organization deployments, and this one runs single-org.
    cards: [...identityAdminSections.access],
  },
  /**
   * Operations — the deployment's moving parts (issue #266, epic #254).
   *
   * A THIRD GROUP rather than more cards under `General`, because the question
   * it answers is a different one. `General` is configuration: values an
   * administrator SETS, which then sit there. These four are the running
   * system: work in flight, machines executing it, and the copy of the data
   * taken while it ran. An operator opens `General` to change something and
   * opens this group to find out what is happening — and putting the two under
   * one heading would make the twelve-card grid a single undifferentiated wall
   * exactly when someone is scanning it during an incident.
   *
   * ===========================================================================
   * ALL FOUR CARDS WERE DECLARED AT ONCE, BEFORE THEIR PAGES EXISTED
   * ===========================================================================
   *
   * `Worker Nodes` and `Database Backup` landed in later issues (#271 routed
   * the first; #287, the epic's last issue, routed the second). Both were
   * declared back in #266 anyway, `disabled: true` and with NO `path`, and the
   * reason is concrete rather than aesthetic:
   *
   * The settings hub is under visual-regression testing at
   * `maxDiffPixels: 4`. Any change to the card grid — a card added to a
   * section, a new section, a title long enough to wrap — reflows the layout
   * and requires the baselines to be regenerated inside a pinned Playwright
   * container. Adding these four cards one issue at a time means doing that
   * four times, with four chances to land a stale or mis-generated baseline,
   * and three of those regenerations would be for a grid nobody has shipped a
   * page behind yet. Declaring the whole group at once makes it ONE reflow and
   * ONE baseline regeneration for the epic.
   *
   * That reasoning is about ADDING a card, and it does not argue against
   * flipping one when its page ships: routing `Worker Nodes` (#271), and then
   * `Database Backup` (#287), changes what a card in the existing grid renders
   * — a "Coming soon" chip becomes a `CardActionArea`, and the rail gains the
   * row it was skipping — so the baselines move once more each time, for a card
   * somebody can now actually open. As of #287 no card in this group is inert,
   * which is the state the group was declared in advance to reach.
   *
   * `disabled: true` AND no `path` together, not either alone, for a card that
   * is still unbuilt. They are belt-and-braces on purpose, because the two
   * consumers treat them differently and both treatments must be right: `SettingsHub` renders an
   * inert card with a "Coming soon" chip and — importantly — no
   * `CardActionArea` at all, so the card is not a tab stop and does not ripple;
   * `NavigationRail` skips the row entirely rather than drawing a link to
   * nowhere. A card carrying a `path` to an unrouted page would instead send a
   * click to `App.tsx`'s `*` catch-all and land the operator on the home page
   * with no explanation.
   *
   * ===========================================================================
   * THE PERMISSIONS ARE THE CONTROLLERS' OWN STRINGS
   * ===========================================================================
   *
   * Per `CLAUDE.md` Settings UI Pattern rule 3, verified against the API rather
   * than assumed:
   *
   *   - `jobs:read`      → `jobs/job-admin.controller.ts` (`PERMISSIONS.JOBS_READ`),
   *                        which both Jobs cards mirror: the list, the stats
   *                        and the insights read all sit behind it. The five
   *                        writes need `jobs:write`, which each PAGE gates
   *                        internally by disabling its controls — the card gate
   *                        is about REACHABILITY, and "what is the queue doing"
   *                        is worth reading for anyone answering "why has
   *                        nothing happened".
   *   - `nodes:read`     → `nodes/nodes-admin.controller.ts`
   *                        (`PERMISSIONS.NODES_READ`), the string it enforces
   *                        on the fleet list, the node detail and the
   *                        credential list; also `node-credential.controller.ts`.
   *                        DELIBERATELY NOT `jobs:read`: `roles.constants.ts`
   *                        splits the two, so a Workers card gated on
   *                        `jobs:read` would advertise a permission that
   *                        controller never checks, and the hub would decide
   *                        reachability on evidence unrelated to whether the
   *                        request behind it will be authorized.
   *   - `db_backup:read` → the backup controller (#268). NOT
   *                        `system_settings:read`: `roles.constants.ts`
   *                        reserves a dedicated `db_backup:read/write/restore`
   *                        triple for this surface precisely so backup access
   *                        can be granted without handing over the settings
   *                        document, and mirroring the settings permission here
   *                        would quietly undo that.
   *
   * All five strings are seeded to ADMIN ONLY (`prisma/seed-data.ts`), so in
   * practice this whole group is invisible to Contributor and Viewer today. The
   * cards still gate per permission and not on the admin ROLE, because a later
   * issue widening one read to an operations role must not have to touch this
   * file — and because a role check here is the split-brain `destinations.ts`'s
   * header exists to describe.
   *
   * The `/admin/settings` route gate is deliberately NOT widened to include
   * `jobs:read`. It mirrors `console`'s `anyPermission` in
   * `config/destinations.ts` byte for byte (asserted in
   * `destinations.test.ts`), and every holder of these permissions is an admin
   * who also holds `system_settings:read`, so nothing is unreachable. Widening
   * one side without the other is exactly the disagreement that test exists to
   * catch.
   */
  {
    label: 'Operations',
    cards: [
      {
        title: 'Jobs',
        description:
          'Inspect the background queue, retry or remove individual jobs, and recover work that stalled.',
        Icon: WorkHistoryOutlinedIcon,
        path: '/admin/settings/jobs',
        permission: 'jobs:read',
      },
      {
        // Nested UNDER the Jobs route, which `settingsPageTitle`'s
        // longest-prefix rule resolves correctly: a bare `startsWith` would let
        // `Jobs` claim this path and title the page "Jobs" in the compact
        // AppBar. That is the case the rule was written for, and it is asserted
        // in `settingsRegistry.test.ts` rather than left to the comment.
        title: 'Job Insights',
        description:
          'See how long the queue takes, how fast it is moving, and when the outstanding work will be done.',
        Icon: QueryStatsIcon,
        path: '/admin/settings/jobs/insights',
        permission: 'jobs:read',
      },
      {
        // Declared inert by #266 alongside the whole group; ROUTED by #271,
        // which ships the page. Flipping a card is exactly the two-field edit
        // the section header describes — a `path` appears and `disabled`
        // disappears — and both consumers pick it up from that alone: the hub
        // swaps its "Coming soon" chip for a real `CardActionArea`, and the
        // Console rail, which skipped the row entirely, starts drawing it.
        //
        // `permission` is UNCHANGED and was already right: `nodes:read` is the
        // literal string `nodes-admin.controller.ts` enforces on its fleet
        // list, its detail read and its credential list
        // (`PERMISSIONS.NODES_READ`). Creating and revoking credentials, and
        // deleting a node, need `nodes:write`, which the PAGE gates internally
        // by omitting the row actions and the create button — the card gate is
        // about REACHABILITY, and "which machines are attached and are they
        // alive" is worth reading for anyone answering "why is nothing being
        // processed".
        title: 'Worker Nodes',
        description:
          'See which machines are attached to this deployment, what they are running, and whether they are healthy.',
        Icon: DnsOutlinedIcon,
        path: '/admin/settings/workers',
        permission: 'nodes:read',
      },
      {
        // Declared inert by #266 alongside the whole group; ROUTED by #287,
        // the last issue of the epic, which ships the page. Flipping a card is
        // the two-field edit the section header describes — a `path` appears
        // and `disabled` disappears — and both consumers pick it up from that
        // alone: the hub swaps its "Coming soon" chip for a real
        // `CardActionArea`, and the Console rail, which skipped the row
        // entirely, starts drawing it. Both of those change the layout.
        //
        // `permission` is UNCHANGED and was already right: `db_backup:read` is
        // the literal string `db-backup/db-backup.controller.ts` enforces on
        // its config read, its run list and its run detail
        // (`PERMISSIONS.DB_BACKUP_READ`). NOT `system_settings:read`:
        // `roles.constants.ts` reserves a dedicated
        // `db_backup:read/write/restore` triple for this surface precisely so
        // backup access can be granted without handing over the settings
        // document, and mirroring the settings permission here would quietly
        // undo that.
        //
        // Scheduling, cancelling and deleting need `db_backup:write`, and
        // restoring or rolling back need `db_backup:restore` — a THIRD
        // permission, kept separate by the API so it can be withheld from
        // someone who may schedule backups but must not be able to replace the
        // database. The PAGE gates both internally by disabling its controls;
        // the card gate is about REACHABILITY, and "is this deployment being
        // backed up, and what have we got" is worth reading for anyone
        // answering "can we recover from this".
        title: 'Database Backup',
        description:
          'Schedule backups, review what has been taken, and restore the database from one.',
        Icon: BackupOutlinedIcon,
        path: '/admin/settings/db-backup',
        permission: 'db_backup:read',
      },
      {
        // Issue #325, epic #319. `broadcasts:read` is the literal string
        // the platform's `notifications/broadcasts/broadcasts.controller` (#470)
        // enforces on its audience count, its list and its detail read
        // (`PERMISSIONS.BROADCASTS_READ`) — the registry never invents a
        // permission, it mirrors one. Composing, scheduling, cancelling,
        // deleting and test-sending need `broadcasts:write`, which the PAGE
        // gates internally by disabling its controls: the card gate is about
        // REACHABILITY, and "what has been announced, and is anything queued to
        // go out" is worth reading for anyone answering "did everyone get told".
        //
        // OPERATIONS, NOT GENERAL, per this section's own header. General holds
        // values an administrator SETS, which then sit there; a broadcast is
        // work you dispatch and then watch — it has a status, a progress
        // counter and a cancel — and it belongs one card away from Jobs, where
        // its fan-out becomes visible.
        //
        // ⚠ NOT THE SAME PAGE AS General → Notifications, and the descriptions
        // are written to keep them apart. That card is the deployment-wide KILL
        // SWITCH: it decides whether browser notifications may be raised at all
        // and which events are suppressed. This one composes and sends a single
        // announcement to every user. Confusing the two during an incident is
        // the difference between silencing every notification in the product
        // and telling everybody what is happening.
        title: 'Broadcasts',
        description:
          'Write an announcement and send it to every active user now or at a scheduled time, then watch it go out.',
        Icon: CampaignOutlinedIcon,
        path: '/admin/settings/broadcasts',
        permission: 'broadcasts:read',
      },
      // Issue #464, epic #462. APPENDED, last in `Operations`: the card is the
      // package's own entry (`system_settings:read` is `DEFAULT_DOCTOR_PERMISSION`,
      // the string the platform's doctor controller enforces on its GET), plus
      // its icon, which the package keeps beside the card.
      { ...doctorSettingsPage.card, Icon: doctorSettingsPage.Icon },
      // #795. `Webhooks`, APPENDED: the deployment's outbound webhook endpoints and
      // their deliveries. `system_webhooks:read` is the SYSTEM permission the packaged
      // `/api/admin/webhooks` controller enforces; writes (`system_webhooks:write`) are gated inside
      // the page. OPERATIONS: deliveries are work dispatched and watched. The slice's
      // `organizations` card is NOT spread (`feature: 'orgs'`; this deployment runs single-org).
      ...webhooksAdminSections.operations,
    ],
  },
  {
    // Issue #471, epic #467. A NEW section APPENDED after Operations rather
    // than three cards inserted into it, so no existing card moves in the hub,
    // the Console rail or the AppBar's title resolver. Observability is the
    // package's own name for this group. `Telemetry` (`telemetry:read`) has no
    // `feature`, deliberately: it is where collection is switched on, so it must
    // be reachable while the feature is off. The Explorer and the Dashboard
    // (`telemetry:query`) exist only while a store is deployed and collection is
    // on. Both nest under the Telemetry route, so the longest-prefix title
    // resolver names them correctly. A viewer without `telemetry:read` or
    // `telemetry:query` sees no section at all.
    label: 'Observability',
    // #795. `Alerts`, APPENDED after the telemetry cards: alert rules and their
    // incidents. `alerts:read` is the SYSTEM permission every read route of the packaged
    // `/api/admin/alerts` controller enforces; writes (`alerts:write`) are gated inside the page.
    // NO `feature`: node rules work while telemetry is off.
    cards: [...telemetryAdminCards, ...alertsAdminCards],
  },
  // Issue #562. `Danger Zone`, PINNED LAST: the one documented exception to
  // append-only (docs/specs/settings-ui.md). A group added later is inserted
  // BEFORE this one, never after; the settings registry test asserts it
  // (`dangerZoneLastViolations`). The packaged factory reset page
  // (`@marinoscar/platform-web/user-data/ui`): `system:factory_reset`, the
  // exact SYSTEM permission the user-data slice's controller enforces (Admin
  // only; never implied by `system_settings:write`). No `feature`. The group
  // sits alone and is never folded into Operations.
  {
    label: DANGER_ZONE_GROUP_LABEL,
    cards: [{ ...factoryResetSettingsPage.card, Icon: factoryResetSettingsPage.Icon }],
  },
];

/**
 * The Console hub itself — the one admin route that owns no card, and so the
 * title `settingsPageTitle` falls back to.
 */
export const ADMIN_HUB_PATH = '/admin/settings';
export const ADMIN_HUB_TITLE = 'Settings';

// PLATFORM-ADOPTION-SHIM(466): the registry TYPES and the two helpers every
// consumer runs (the hub, the rail, the AppBar title resolver) are the
// package's now (`@marinoscar/platform-web/settings/ui`). They are re-exported
// from here so the three consumers keep one import site and one gate; the
// shim goes when #484 moves those consumers onto the package's shell.
export { settingsPageTitle, visibleSettingsSections } from '@marinoscar/platform-web/settings/ui';
export type { SettingsCardDef, SettingsSectionDef } from '@marinoscar/platform-web/settings/ui';
